import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Music2, Camera, Search } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { useLookup } from "@/hooks/useLookup";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export function TrackSoundDialog({ open, onOpenChange, artistId, artistName, onCreated }) {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState(artistName || "");
  const [soundUrl, setSoundUrl] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [cover, setCover] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSongs, setShowSongs] = useState(false);
  const { results: songs, loading: songLoading } = useLookup("songs", title);

  const detectPlatform = (url) => {
    const u = url.toLowerCase();
    if (u.includes("tiktok")) setPlatform("tiktok");
    else if (u.includes("instagram")) setPlatform("instagram");
  };

  const pickSong = (s) => {
    setTitle(s.track || title);
    if (!artistId && s.artist) setArtist(s.artist);
    if (s.artwork) setCover(s.artwork);
    if (s.release_date) setStartDate(s.release_date);
    setShowSongs(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCover(fileUrl(data.url));
      toast.success("Cover art uploaded");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Add a song title");
    if (!artistId && !artist.trim()) return toast.error("Add an artist name");
    setSaving(true);
    try {
      const { data } = await api.post("/campaigns", {
        artist_id: artistId || undefined,
        artist_name: artistId ? undefined : artist.trim(),
        title: title.trim(),
        platform,
        sound_url: soundUrl.trim(),
        cover_art: cover,
        start_date: startDate,
      });
      toast.success("Sound tracked");
      onOpenChange(false);
      setTitle(""); setSoundUrl(""); setCover("");
      onCreated?.(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="track-sound-dialog" className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Track a Sound</DialogTitle>
          <DialogDescription>Paste a TikTok sound or Instagram audio/reel URL, or search a song to auto-fill.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Sound URL</Label>
            <Input
              data-testid="input-sound-url"
              placeholder="https://www.tiktok.com/music/... or instagram.com/reel/..."
              value={soundUrl}
              onChange={(e) => { setSoundUrl(e.target.value); detectPlatform(e.target.value); }}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <Label className="flex items-center gap-1"><Search className="h-3 w-3" /> Song title</Label>
              <Input
                data-testid="input-song-title"
                value={title}
                onChange={(e) => { setTitle(e.target.value); setShowSongs(true); }}
                onFocus={() => setShowSongs(true)}
                onBlur={() => setTimeout(() => setShowSongs(false), 150)}
                className="mt-1"
                autoComplete="off"
                placeholder="Search a song…"
              />
              {showSongs && (songLoading || songs.length > 0) && (
                <div data-testid="song-suggestions" className="absolute z-50 mt-1 max-h-64 w-[calc(200%+0.75rem)] overflow-auto rounded-xl border border-neutral-200 bg-white p-1 shadow-xl">
                  {songLoading && <div className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…</div>}
                  {songs.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`song-suggestion-${i}`}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); pickSong(s); }}
                      className="flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-neutral-100"
                    >
                      {s.artwork ? <img src={s.artwork} alt="" className="h-9 w-9 rounded object-cover" /> : <span className="grid h-9 w-9 place-items-center rounded bg-neutral-100"><Music2 className="h-4 w-4 text-neutral-400" /></span>}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{s.track}</span>
                        <span className="block truncate text-xs text-neutral-500">{s.artist}{s.genre ? ` · ${s.genre}` : ""}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Artist</Label>
              <Input data-testid="input-artist" value={artist} disabled={!!artistId} onChange={(e) => setArtist(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Platform</Label>
              <div className="mt-1 flex gap-2">
                {["instagram", "tiktok"].map((p) => (
                  <button
                    key={p}
                    data-testid={`platform-choice-${p}`}
                    type="button"
                    onClick={() => setPlatform(p)}
                    className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2.5 text-sm font-medium transition-all ${
                      platform === p ? "border-[#35606E] bg-[#35606E] text-white" : "border-neutral-200 bg-white text-neutral-600"
                    }`}
                  >
                    {p === "tiktok" ? <Music2 className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
                    {p === "tiktok" ? "TikTok" : "Reels"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Start date</Label>
              <Input data-testid="input-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label>Cover art (optional)</Label>
            <label className="mt-1 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-neutral-300 p-3 transition-colors hover:bg-neutral-50">
              {cover ? (
                <img src={cover} alt="cover" className="h-12 w-12 rounded-lg object-cover" />
              ) : (
                <span className="grid h-12 w-12 place-items-center rounded-lg bg-neutral-100 text-neutral-400">
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                </span>
              )}
              <span className="text-sm text-neutral-500">{cover ? "Change cover" : "Upload or auto-filled from song search"}</span>
              <input data-testid="input-cover-upload" type="file" accept="image/*" className="hidden" onChange={handleUpload} />
            </label>
          </div>
          <Button data-testid="submit-track-sound" onClick={submit} disabled={saving} className="w-full rounded-full bg-[#35606E] py-6 text-base hover:bg-[#294b57]">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Track Sound
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
